<template>
  <ChartLayout
    :title="`${title}${
      selectedProduct
        ? `, ${selectedProduct}`
        : ` by ${productLabel.toLowerCase()}`
    }, ${selectedRegion}`"
    :units="units"
  >
    <template #params>
      <ControlGroup>
        <Dropdown
          :label="productLabel"
          :options="productOptions"
          v-model="selectedProduct"
          resetOnInvalid
          small
        />
      </ControlGroup>
    </template>
    <Chart
      key="capgrowth"
      :options="
        buildOptions(
          {
            series: getFiveYearGrowth(flowData),
            chart: {
              height: 400,
            },
            xAxis: {
              type: 'category',
              plotBands: [
                {
                  from: periods.length - 2 + 0.5,
                  to: periods.length + 0.5,
                  label: {
                    text: 'Forecast',
                  },
                },
              ],
            },
            yAxis: {
              plotLines: [
                {
                  value: 0,
                  className: 'highcharts-zero-axis',
                  zIndex: 999,
                },
              ],
            },
            tooltip: {
              valueSuffix: ` ${units}`,
            },
            legend: {
              enabled: !selectedProduct,
              align: 'right',
              verticalAlign: 'middle',
              layout: 'vertical',
              width: '20%',
              floating: false,
              reversed: true,
            },
          },
          'column'
        )
      "
    />
    <template #notes v-if="note">
      {{ note }}
    </template>
  </ChartLayout>
</template>

<script>
import { ControlGroup, Dropdown } from "@international-energy-agency/vue-ui";
import { Chart } from "highcharts-vue";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    selectedRegion: {
      type: String,
      required: true,
    },
    products: {
      type: Array,
      required: true,
    },
    productLabel: {
      type: String,
      default: "Product",
    },
    defaultProduct: {
      type: String,
      required: false,
    },
    selectedFlow: {
      type: String,
      required: false,
    },
    // Allow selecting "all" products
    showAll: {
      type: Boolean,
      default: false,
    },
    // The list of "all" products; defaults to all the selectable products
    allProducts: {
      type: Array,
      required: false,
    },
    // Compute y-o-y growth, not needed if passing e.g. net additions
    computeGrowth: {
      type: Boolean,
      default: false,
    },
    // override for different periods
    periods: {
      type: Array,
      default: () => [
        [2011, 2016],
        [2017, 2022],
        [2023, 2028],
      ],
    },
    data: {
      type: Array,
      required: true,
    },
    note: {
      type: String,
      default: null,
    },
  },
  components: { Chart, ChartLayout, ControlGroup, Dropdown },
  data() {
    return {
      selectedProduct: this.defaultProduct || null,
    };
  },
  computed: {
    flowData() {
      const { data, selectedFlow } = this;
      return selectedFlow
        ? data.filter((row) => row.flow === selectedFlow)
        : data;
    },
    units() {
      const { flowData } = this;
      return flowData?.length ? flowData[0].unit : "";
    },
    productOptions() {
      const { products, showAll } = this;
      return showAll
        ? [
            { label: "All", value: null },
            ...products.map((value) => ({ label: value, value })),
          ]
        : products;
    },
  },
  methods: {
    buildOptions: ChartOptions.buildOptions,
    getGrowth(startYear, endYear, data) {
      const firstYear =
        data.find(
          (r) => parseInt(r.year) === startYear - 1 /* RED crazy person math */
        )?.value || 0;
      const lastYear =
        data.find((r) => parseInt(r.year) === endYear)?.value || 0;
      return parseFloat(lastYear) - parseFloat(firstYear);
    },
    getFiveYearGrowthByProduct(data, product) {
      const { products, selectedFlow, computeGrowth, getGrowth, periods } =
        this;
      const dataRows = data.filter((r) => r.product === product);
      const historicalPeriods = periods.slice(0, -1);
      const forecastPeriod = periods[periods.length - 1];
      // Manually compute growth
      if (computeGrowth) {
        const growthData = historicalPeriods.map((period) => [
          `${period[0]}-${period[1]}`,
          getGrowth(period[0], period[1], dataRows),
        ]);
        const hasAccelerated = data.some(
          (r) => r.flow === selectedFlow && r.case === "ACC"
        );
        if (hasAccelerated) {
          growthData.push([
            `${forecastPeriod[0]}-${forecastPeriod[1]}<br>Main case`,
            getGrowth(
              forecastPeriod[0],
              forecastPeriod[1],
              dataRows.filter((r) => r.case === "MAIN")
            ),
          ]);
          growthData.push([
            `${forecastPeriod[0]}-${forecastPeriod[1]}<br>Accelerated case`,
            getGrowth(
              forecastPeriod[0],
              forecastPeriod[1],
              dataRows.filter((r) => r.case === "ACC")
            ),
          ]);
        } else {
          growthData.push([
            `${forecastPeriod[0]}-${forecastPeriod[1]}`,
            getGrowth(forecastPeriod[0], forecastPeriod[1], dataRows),
          ]);
        }
        return {
          name: product,
          colorIndex: products.indexOf(product) % 10,
          legendIndex: products.indexOf(product) % 10,
          data: growthData,
        };
      }
      // Otherwise just sum it
      return {
        name: product,
        colorIndex: products.indexOf(product) % 10,
        legendIndex: products.indexOf(product) % 10,
        data: [
          ...historicalPeriods.map((period) => [
            `${period[0]}-${period[1]}`,
            dataRows.reduce(
              (total, r) =>
                r.case === "MAIN" && r.year >= period[0] && r.year <= period[1]
                  ? total + r.value
                  : total,
              0
            ),
          ]),
          [
            `${forecastPeriod[0]}-${forecastPeriod[1]}<br>Main case`,
            dataRows.reduce(
              (total, r) =>
                r.case === "MAIN" &&
                r.year >= forecastPeriod[0] &&
                r.year <= forecastPeriod[1]
                  ? total + r.value
                  : total,
              0
            ),
          ],
          [
            `${forecastPeriod[0]}-${forecastPeriod[1]}<br>Accelerated case`,
            dataRows.reduce(
              (total, r) =>
                r.case === "ACC" &&
                r.year >= forecastPeriod[0] &&
                r.year <= forecastPeriod[1]
                  ? total + r.value
                  : total,
              0
            ),
          ],
        ],
      };
    },
    getFiveYearGrowth(data) {
      if (!data) return data;
      const { products, allProducts, selectedProduct } = this;
      if (selectedProduct)
        return [this.getFiveYearGrowthByProduct(data, selectedProduct)];
      return (allProducts || products)
        .filter((product) => product.length > 0)
        .map((product) => this.getFiveYearGrowthByProduct(data, product));
    },
  },
};
</script>

<style>
.m-chart__params .control-group {
  margin-bottom: 0px;
}
</style>